import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import hifiBackground from '../assets/hifi.webp';  // Add this missing import
// First import shared styles
import './Marketplace.css';
// Then import component-specific styles
import './MarketplaceList.css';

const MarketplaceList = () => {
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMyListings, setLoadingMyListings] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    shipping: null,
    negotiable: null,
    city: ''
  });
  const [showMyListings, setShowMyListings] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasExpiredListings, setHasExpiredListings] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // Add state to track subcategories
  const [subcategories, setSubcategories] = useState({});

  // Add these states to keep track of both main categories and all categories
  const [allCategories, setAllCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);

  // Add loading state
  const [myListingsLoading, setMyListingsLoading] = useState(false);

  // 1. Add this helper function at the top of your component, before the return statement
  const isNegotiable = (listing) => {
    if (!listing) return false;
    
    // Check different possible formats
    return listing.is_negotiable === 'Y' || 
           listing.is_negotiable === true || 
           listing.is_negotiable === 'true' || 
           listing.is_negotiable === 1 || 
           listing.is_negotiable === '1';
  }

  useEffect(() => {
    // Prüfe, ob der Benutzer eingeloggt ist
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    
    const fetchListings = async () => {
      try {
        setLoading(true);
        
        // Always include token when available
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch('/api/marketplace', { headers });
        
        if (!response.ok) {
          throw new Error('Fehler beim Laden der Angebote');
        }
        
        const data = await response.json();
        setListings(data);
        console.log(`Loaded ${data.length} marketplace listings`);
      } catch (error) {
        console.error('Error fetching listings:', error);
        setError('Fehler beim Laden der Angebote');
      } finally {
        setLoading(false);
      }
    };
    
    fetchListings();
    
    // Wenn der Benutzer eingeloggt ist, hole auch seine eigenen Angebote
    if (token) {
      fetchMyListings();
    }
  }, []);

  useEffect(() => {
    // Fetch only main categories
    const fetchCategories = async () => {
      try {
        // Add main_only=true parameter to only get main categories
        const response = await fetch('/api/marketplace/categories?main_only=true');
        if (!response.ok) {
          throw new Error('Failed to load categories');
        }
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Update useEffect to also fetch the category hierarchy
  useEffect(() => {
    // Fetch category hierarchy
    const fetchCategoryHierarchy = async () => {
      try {
        const response = await fetch('/api/marketplace/categories');
        if (!response.ok) {
          throw new Error('Failed to load category hierarchy');
        }
        const data = await response.json();
        
        // Create mapping of parent categories to their subcategories
        const subCatMap = {};
        data.forEach(cat => {
          if (cat.parent_id) {
            if (!subCatMap[cat.parent_id]) {
              subCatMap[cat.parent_id] = [];
            }
            subCatMap[cat.parent_id].push(cat.id);
          }
        });
        
        setSubcategories(subCatMap);
      } catch (error) {
        console.error('Error fetching category hierarchy:', error);
      }
    };
    
    fetchCategoryHierarchy();
  }, []);

  // Update the useEffect that fetches categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // 1. Fetch ALL categories first (we need this for filtering)
        const response = await fetch('/api/marketplace/categories');
        if (!response.ok) {
          throw new Error('Failed to load categories');
        }
        
        const allCategoriesData = await response.json();
        setAllCategories(allCategoriesData);
        
        // 2. Filter to get ONLY main categories (those without parent_id)
        const mainCategoriesOnly = allCategoriesData.filter(cat => !cat.parent_id);
        setMainCategories(mainCategoriesOnly);
        
        console.log('Main categories:', mainCategoriesOnly);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Add this useEffect to ensure auth state is correctly tracked:
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
    };
    
    checkAuth();
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  // Eigene Angebote abrufen, inklusive inaktiver
  const fetchMyListings = async () => {
    try {
      setMyListingsLoading(true); // Add a loading state
      
      const token = localStorage.getItem('token');
      
      // Don't attempt to fetch if not authenticated
      if (!token) {
        console.log('User not authenticated, skipping my listings fetch');
        setMyListings([]);
        setMyListingsLoading(false);
        return;
      }
      
      console.log('Fetching user listings with token');
      
      // Add timeout to prevent long hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      try {
        const response = await fetch('/api/marketplace/user/listings', {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // Handle specific status codes
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication issue, clearing token');
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setMyListings([]);
          setMyListingsLoading(false);
          return;
        }
        
        if (response.status === 404) {
          console.log('User not found in database');
          setMyListings([]);
          setMyListingsLoading(false);
          return;
        }
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API error response:', errorText);
          throw new Error('Fehler beim Laden Ihrer Angebote');
        }
        
        const data = await response.json();
        console.log(`Successfully loaded ${data.length} user listings`);
        setMyListings(data);
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Request timed out');
          throw new Error('Anfrage hat zu lange gedauert');
        }
        throw err;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error('Error fetching my listings:', error);
      // Don't break the UI for this non-critical feature
      setMyListings([]);
    } finally {
      setMyListingsLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };

  const resetFilters = () => {
    setFilters({
      minPrice: '',
      maxPrice: '',
      shipping: null,
      negotiable: null,
      city: ''
    });
    setSearchTerm('');
    setSelectedCategory(null);
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? null : categoryId);
  };

  // Entscheide, welche Listings angezeigt werden sollen
  const displayListings = showMyListings ? myListings : listings;

  // Replace the existing filteredListings with this comprehensive version
  const filteredListings = displayListings.filter(listing => {
    // Start with search term filter
    if (searchTerm && !listing.title?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !listing.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Price range filters
    if (filters.minPrice && parseFloat(listing.price) < parseFloat(filters.minPrice)) {
      return false;
    }
    
    if (filters.maxPrice && parseFloat(listing.price) > parseFloat(filters.maxPrice)) {
      return false;
    }
    
    // Shipping filter
    if (filters.shipping !== null) {
      const hasShipping = listing.shipping_available === 'Y' || 
                         listing.shipping_available === true || 
                         listing.shipping_available === 'true';
      if (filters.shipping !== hasShipping) {
        return false;
      }
    }
    
    // Negotiable filter
    if (filters.negotiable !== null) {
      // Use our helper function to ensure consistent checking
      const isItemNegotiable = isNegotiable(listing);
      if (filters.negotiable !== isItemNegotiable) {
        return false;
      }
    }
    
    // Location filter - check both city and ZIP
    if (filters.city && 
        !listing.city?.toLowerCase().includes(filters.city.toLowerCase()) && 
        !listing.zip?.includes(filters.city)) {
      return false;
    }
    
    // Category filter - keep the existing logic
    if (selectedCategory !== null) {
      // Direct category match
      if (listing.category_id === selectedCategory) {
        return true;
      }
      
      // Subcategory match
      const isSubcategory = allCategories.some(cat => 
        cat.id === listing.category_id && cat.parent_id === selectedCategory
      );
      
      if (!isSubcategory) {
        return false;
      }
    }
    
    // If it passed all filters, include it
    return true;
  });

  // Berechne das Ablaufdatum für die Anzeige
  const calculateExpiryDate = (created_at) => {
    const createdDate = new Date(created_at);
    const expiryDate = new Date(createdDate);
    expiryDate.setDate(expiryDate.getDate() + 60);
    
    return expiryDate.toLocaleDateString('de-DE');
  };

  const getCategoryIcon = (categoryName) => {
    const nameLC = categoryName.toLowerCase();
    
    if (nameLC.includes('lautsprecher')) return 'speaker';
    if (nameLC.includes('verstärker') || nameLC.includes('verstarker')) return 'equalizer';
    if (nameLC.includes('plattenspieler') || nameLC.includes('vinyl')) return 'album';
    if (nameLC.includes('cd')) return 'music_note';
    if (nameLC.includes('netzwerk') || nameLC.includes('streamer')) return 'router';
    if (nameLC.includes('kopfhörer') || nameLC.includes('kopfhorer')) return 'headphones';
    if (nameLC.includes('kabel')) return 'cable';
    if (nameLC.includes('zubehör') || nameLC.includes('zubehor')) return 'home_repair_service';
    
    // Default icon
    return 'devices_other';
  };

  if (loading) {
    return (
      <div className="marketplace-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Lade Audio-Gebrauchtmarkt Angebote...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="marketplace-container">
        <div className="error-message">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-container">
      <header 
        className="marketplace-header"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${hifiBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="header-content">
          <h1>Audio-Gebrauchtmarkt</h1>
          <p>Hochwertige HiFi-Komponenten von Privat an Privat</p>
          
          {isAuthenticated ? (
            <Link to="/marketplace/new" className="create-listing-button">
              <span className="material-symbols-outlined">add_circle</span>
              Neues Angebot einstellen
            </Link>
          ) : (
            <div className="auth-required-container">
              <p>Um ein Angebot einzustellen, müssen Sie angemeldet sein</p>
              <div className="auth-buttons">
                <Link to="/login" className="auth-button login">
                  <span className="material-symbols-outlined">login</span>
                  Anmelden
                </Link>
                <Link to="/signup" className="auth-button register">  {/* Changed from /register to /signup */}
                  <span className="material-symbols-outlined">person_add</span>
                  Registrieren
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>
      
      {/* UPDATED: Only render main category buttons */}
      <div className="category-filters">
        <button
          className={`category-button ${selectedCategory === null ? 'active' : ''}`}
          onClick={() => handleCategorySelect(null)}
        >
          <span className="material-symbols-outlined">category</span>
          Alle Kategorien
        </button>
        
        {/* Use mainCategories instead of categories */}
        {mainCategories.map(category => (
          <button
            key={category.id}
            className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => handleCategorySelect(category.id)}
          >
            <span className="material-symbols-outlined">{getCategoryIcon(category.name)}</span>
            {category.name}
          </button>
        ))}
      </div>

      {showMyListings && hasExpiredListings && (
        <div className="expired-listings-notice">
          <span className="material-symbols-outlined">schedule</span>
          <p>
            Sie haben abgelaufene Angebote, die nicht mehr öffentlich sichtbar sind. 
            <br/>Diese Angebote werden mit <span className="expired-label">Abgelaufen</span> gekennzeichnet.
          </p>
        </div>
      )}
      
      <div className="marketplace-search-filter">
        <div className="search-container">
          <span className="material-symbols-outlined">search</span>
          <input
            type="text"
            placeholder="Suche nach Angeboten..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filters-container">
          <div className="filter-group">
            <label>Preis von:</label>
            <input
              type="number"
              min="0"
              placeholder="Min €"
              value={filters.minPrice}
              onChange={(e) => handleFilterChange('minPrice', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label>bis:</label>
            <input
              type="number"
              min="0"
              placeholder="Max €"
              value={filters.maxPrice}
              onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label>Versand:</label>
            <select
              value={filters.shipping === null ? "" : filters.shipping.toString()}
              onChange={(e) => {
                const value = e.target.value === "" ? null : e.target.value === "true";
                handleFilterChange('shipping', value);
              }}
            >
              <option value="">Alle</option>
              <option value="true">Ja</option>
              <option value="false">Nur Abholung</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Verhandelbar:</label>
            <select
              value={filters.negotiable === null ? "" : filters.negotiable.toString()}
              onChange={(e) => {
                const value = e.target.value === "" ? null : e.target.value === "true";
                handleFilterChange('negotiable', value);
              }}
            >
              <option value="">Alle</option>
              <option value="true">Ja</option>
              <option value="false">Nein</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Ort/PLZ:</label>
            <input
              type="text"
              placeholder="Stadt oder PLZ"
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
            />
          </div>
          
          <button onClick={resetFilters} className="reset-filters-button">
            <span className="material-symbols-outlined">restart_alt</span>
            Filter zurücksetzen
          </button>
        </div>
      </div>
      
      <div className="marketplace-listings">
        {filteredListings.length === 0 ? (
          <div className="no-listings">
            <span className="material-symbols-outlined">inventory_2</span>
            <p>
              {showMyListings 
                ? 'Sie haben noch keine Angebote erstellt.' 
                : 'Keine Angebote gefunden, die Ihren Suchkriterien entsprechen.'}
            </p>
            {showMyListings && (
              <Link to="/marketplace/new" className="create-first-listing-button">
                <span className="material-symbols-outlined">add</span>
                Erstes Angebot erstellen
              </Link>
            )}
          </div>
        ) : (
          filteredListings.map(listing => (
            <div key={listing.id} className={`listing-card ${!listing.is_active ? 'expired' : ''}`}>
              <div className="listing-image">
                {listing.thumbnail ? (
                  <img src={listing.thumbnail} alt={listing.title} />
                ) : (
                  <div className="no-image">Kein Bild</div>
                )}
                {listing.is_private && (
                  <div className="private-badge">
                    <span className="material-symbols-outlined">lock</span>
                    Privat
                  </div>
                )}
                {listing.is_owner && (
                  <div className="owner-badge">
                    <span className="material-symbols-outlined">person</span>
                    Mein Angebot
                  </div>
                )}
                {showMyListings && !listing.is_active && (
                  <div className="expired-badge">
                    <span className="material-symbols-outlined">schedule</span>
                    Abgelaufen
                  </div>
                )}
              </div>

              <div className="listing-content">
                <h3>{listing.title}</h3>
                <div className="listing-details">
                  <span className="listing-price">
                    {parseFloat(listing.price).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                    {isNegotiable(listing) ? (
                      <span className="negotiable-badge">VB</span>
                    ) : (
                      <span className="fixed-price-badge">FP</span>
                    )}
                  </span>
                  <span className="listing-id">ID: {listing.id}</span>
                  {/* Add this to show seller trust badge */}
                  {listing.seller_trust_status && (
                    <span className={`seller-mini-badge ${listing.seller_trust_status.toLowerCase()}`}>
                      <span className="material-symbols-outlined">
                        {listing.seller_trust_status === 'TRUSTED' ? 'verified' : 'gpp_maybe'}
                      </span>
                    </span>
                  )}
                </div>
                <p className="listing-location">
                  <span className="material-symbols-outlined">location_on</span>
                  {listing.city}, {listing.zip}
                </p>
                <p className="listing-date">
                  <span className="material-symbols-outlined">event</span>
                  Eingestellt: {new Date(listing.created_at).toLocaleDateString('de-DE')}
                </p>
                <div className="listing-features">
                  {listing.shipping_available === 'Y' && (
                    <span className="feature shipping-available">
                      <span className="material-symbols-outlined">local_shipping</span>
                      {listing.pickup_preferred === 'Y' ? 'Versand & Abholung bevorzugt' : 'Versand möglich'}
                    </span>
                  )}
                  
                  {listing.shipping_available !== 'Y' && (
                    <span className="feature pickup-only">
                      <span className="material-symbols-outlined">store</span>
                      Nur Abholung
                    </span>
                  )}
                  
                  {listing.phone_number && (listing.show_phone_to === 'A' || 
                    (listing.show_phone_to === 'R' && isAuthenticated)) && (
                    <span className="feature phone-available">
                      <span className="material-symbols-outlined">phone</span>
                      Tel. verfügbar
                    </span>
                  )}
                </div>
                
                {/* Details ansehen Button im konsistenten Look & Feel */}
                <div className="listing-actions">
                  <Link to={`/marketplace/${listing.id}`} className="btn primary-btn details-btn">
                    <span className="material-symbols-outlined">visibility</span>
                    Details ansehen
                  </Link>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {isAuthenticated && (
        <div className="my-listings-section">
          <h2>Meine Angebote</h2>
          {myListingsLoading ? (
            <div className="loading-indicator">Lade Ihre Angebote...</div>
          ) : myListings.length > 0 ? (
            <div className="marketplace-grid">
              {myListings.map(listing => (
                <div key={listing.id} className="marketplace-item">
                  <Link to={`/marketplace/${listing.id}`} className="marketplace-link">
                    <div className="listing-image">
                      {listing.thumbnail ? (
                        <img src={listing.thumbnail} alt={listing.title} />
                      ) : (
                        <div className="no-image">Kein Bild</div>
                      )}
                    </div>
                    <div className="listing-details">
                      <h3>{listing.title}</h3>
                      <p className="price">{listing.price} €</p>
                    </div>
                  </Link>
                  <div className="listing-actions">
                    <Link to={`/marketplace/edit/${listing.id}`} className="btn btn-edit">
                      <span className="material-symbols-outlined">edit</span>
                    </Link>
                    <button onClick={() => handleDelete(listing.id)} className="btn btn-delete">
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>Sie haben noch keine Angebote eingestellt.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketplaceList;